import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type {
  Agreement,
  Onboarding,
  OnboardingStepState,
  OnboardingStepStatus,
} from '@catalyst-psa/shared';
import {
  ONBOARDING_AGREEMENT_STEP,
  ONBOARDING_PHASES,
  ONBOARDING_PROJECT_STEP,
  ONBOARDING_STEP_STATUSES,
  WELCOME_SECTIONS,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { mutationError } from '../../api/client.js';
import { formatDate } from '../../utils/date.js';
import { OPPORTUNITY_STATUS_LABELS, OPPORTUNITY_STATUS_TONES } from '../opportunities/status.js';
import { useUpdateOpportunity } from '../opportunities/api.js';
import { AgreementFormModal } from '../agreements/AgreementFormModal.js';
import { ProjectFormModal } from '../projects/ProjectFormModal.js';
import { OnboardingNotesSection } from './OnboardingNotesSection.js';
import { OnboardingLinksTab } from './OnboardingLinksTab.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { MsGroupModal } from './MsGroupModal.js';
import { SendWelcomeEmailModal } from './SendWelcomeEmailModal.js';
import { SalesRepRequestModal } from './SalesRepRequestModal.js';
import { EngagePmoModal } from './EngagePmoModal.js';
import { CreateCuProjectModal } from './CreateCuProjectModal.js';
import {
  useDeleteOnboarding,
  useDisableWelcomeLink,
  useOnboarding,
  useRegenerateWelcomeLink,
  useSetOnboardingException,
  useSetOnboardingPhase,
  useUpdateOnboardingLinks,
  useUpdateOnboardingStep,
  useUpdateWelcomeSections,
} from './api.js';
import {
  ONBOARDING_PHASE_LABELS,
  ONBOARDING_PHASE_TONES,
  ONBOARDING_STATUS_LABELS,
  ONBOARDING_STATUS_TONES,
  ONBOARDING_STEP_STATUS_LABELS,
  ONBOARDING_STEP_STATUS_TONES,
} from './status.js';

interface OnboardingDetailModalProps {
  onboardingId: string | null;
  onClose: () => void;
  onDeleted?: () => void;
}

const STATUS_BUTTON_LABEL: Record<OnboardingStepStatus, string> = {
  pending: 'Pending',
  completed: 'Done',
  skipped: 'Skip',
};

/** External tool URLs opened from the SPAM / LastPass onboarding steps. */
const SPAM_URL = 'https://cloudv-ui.spamtitan.com/customer/filtering/allow_block_list/domains';
const LASTPASS_URL = 'https://lastpass.com/vault/';

function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Exception toggle badge plus its notes field. The badge blends into the background while
 * off and turns red once flagged; notes are only shown (and stored) while it is on.
 */
function ExceptionSection({
  onboarding,
  canEdit,
}: {
  onboarding: Onboarding;
  canEdit: boolean;
}): JSX.Element {
  const setException = useSetOnboardingException();
  const [notes, setNotes] = useState(onboarding.exceptionNotes ?? '');

  useEffect(() => {
    setNotes(onboarding.exceptionNotes ?? '');
  }, [onboarding.exceptionNotes]);

  const isOn = onboarding.isException;
  const notesDirty = notes.trim() !== (onboarding.exceptionNotes ?? '');

  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={!canEdit || setException.isPending}
        aria-pressed={isOn}
        onClick={() =>
          setException.mutate({
            id: onboarding.id,
            input: { isException: !isOn, exceptionNotes: isOn ? null : notes },
          })
        }
        className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition disabled:cursor-not-allowed ${
          isOn
            ? 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700'
            : 'border-slate-200 bg-transparent text-slate-400 hover:border-slate-300 hover:text-slate-600'
        }`}
      >
        Exception
      </button>
      {isOn ? (
        <div>
          <label
            htmlFor="onboarding-exception-notes"
            className="text-xs uppercase tracking-wide text-slate-500"
          >
            Exception Notes
          </label>
          <textarea
            id="onboarding-exception-notes"
            rows={3}
            value={notes}
            disabled={!canEdit}
            placeholder="Why is this onboarding an exception?"
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50"
          />
          {canEdit ? (
            <div className="mt-2 flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                isLoading={setException.isPending}
                disabled={!notesDirty}
                onClick={() =>
                  setException.mutate({
                    id: onboarding.id,
                    input: { isException: true, exceptionNotes: notes },
                  })
                }
              >
                Save notes
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface DetailItemProps {
  label: string;
  children: ReactNode;
}

function DetailItem({ label, children }: DetailItemProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{children}</dd>
    </div>
  );
}

function ProgressBar({ onboarding }: { onboarding: Onboarding }): JSX.Element {
  const total = onboarding.totalSteps || 1;
  const completedPct = (onboarding.completedCount / total) * 100;
  const skippedPct = (onboarding.skippedCount / total) * 100;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
        <span>
          {onboarding.completedCount} completed · {onboarding.skippedCount} skipped ·{' '}
          {onboarding.pendingCount} pending
        </span>
        <span className="font-medium text-slate-700">{onboarding.progressPercent}%</span>
      </div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-emerald-500" style={{ width: `${completedPct}%` }} />
        <div className="h-full bg-sky-400" style={{ width: `${skippedPct}%` }} />
      </div>
    </div>
  );
}

function StepRow({
  step,
  canEdit,
  isUpdating,
  onSetStatus,
  onCreate,
  createLabel,
  detail,
  emailSentAt,
}: {
  step: OnboardingStepState;
  canEdit: boolean;
  isUpdating: boolean;
  onSetStatus: (status: OnboardingStepStatus) => void;
  onCreate: (() => void) | null;
  createLabel?: string;
  detail?: string | null;
  /** When set, the step's outgoing email has been sent (shows an "Email sent" badge). */
  emailSentAt?: string | null;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <Badge tone={ONBOARDING_STEP_STATUS_TONES[step.status]}>
          {ONBOARDING_STEP_STATUS_LABELS[step.status]}
        </Badge>
        <span className="text-sm font-medium text-slate-800">{step.label}</span>
        {emailSentAt ? (
          <span className="inline-flex items-center gap-1.5">
            <Badge tone="blue">Email sent</Badge>
            <span className="text-xs text-slate-500">{formatDate(emailSentAt)}</span>
          </span>
        ) : null}
        {detail ? <span className="text-xs text-slate-500">{detail}</span> : null}
      </div>
      <div className="flex items-center gap-1">
        {onCreate ? (
          <Button variant="secondary" className="px-2 py-1 text-xs" onClick={onCreate}>
            {createLabel ?? 'Create…'}
          </Button>
        ) : null}
        {canEdit ? (
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            {ONBOARDING_STEP_STATUSES.map((status) => {
              const active = step.status === status;
              return (
                <button
                  key={status}
                  type="button"
                  disabled={isUpdating}
                  onClick={() => onSetStatus(status)}
                  className={`px-2.5 py-1 text-xs font-medium ${
                    active ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  } ${status !== 'pending' ? 'border-l border-slate-200' : ''}`}
                >
                  {STATUS_BUTTON_LABEL[status]}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function OnboardingDetailModal({
  onboardingId,
  onClose,
  onDeleted,
}: OnboardingDetailModalProps): JSX.Element | null {
  const { can, isAdmin } = useAuth();
  const navigate = useNavigate();
  const canEdit = can('sales', 'edit');
  const canDelete = can('sales', 'delete') || isAdmin;

  const [tab, setTab] = useState('details');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [msGroupOpen, setMsGroupOpen] = useState(false);
  const [welcomeEmailOpen, setWelcomeEmailOpen] = useState(false);
  const [salesRepRequestOpen, setSalesRepRequestOpen] = useState(false);
  const [engagePmoOpen, setEngagePmoOpen] = useState(false);
  const [cuProjectOpen, setCuProjectOpen] = useState(false);
  const [welcomeCopied, setWelcomeCopied] = useState(false);
  const [confirmRegenWelcome, setConfirmRegenWelcome] = useState(false);
  const [confirmDisableWelcome, setConfirmDisableWelcome] = useState(false);

  const queryClient = useQueryClient();
  const onboardingQuery = useOnboarding(onboardingId ?? '');
  const updateStep = useUpdateOnboardingStep();
  const updateLinks = useUpdateOnboardingLinks();
  const updateOpportunity = useUpdateOpportunity();
  const setPhase = useSetOnboardingPhase();
  const deleteMutation = useDeleteOnboarding();
  const regenWelcome = useRegenerateWelcomeLink();
  const disableWelcome = useDisableWelcomeLink();
  const updateWelcomeSections = useUpdateWelcomeSections();

  const onboarding = onboardingQuery.data;

  if (!onboardingId) return null;

  const handleSetStatus = (stepKey: string, status: OnboardingStepStatus): void => {
    updateStep.mutate({ id: onboardingId, stepKey, status });
  };

  const handleProjectCreated = (): void => {
    updateStep.mutate({ id: onboardingId, stepKey: ONBOARDING_PROJECT_STEP, status: 'completed' });
  };

  const handleAgreementCreated = (agreement: Agreement): void => {
    updateLinks.mutate({ id: onboardingId, input: { agreementId: agreement.id } });
    updateStep.mutate({
      id: onboardingId,
      stepKey: ONBOARDING_AGREEMENT_STEP,
      status: 'completed',
    });
  };

  const handleMarkOpportunityWon = (): void => {
    if (!onboarding) return;
    updateOpportunity.mutate(
      { id: onboarding.opportunityId, input: { stage: 'won', status: 'won' } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
        },
      },
    );
  };

  const welcomeUrl =
    onboarding?.welcomeToken && typeof window !== 'undefined'
      ? `${window.location.origin}/w/${onboarding.welcomeToken}`
      : '';

  const handleCopyWelcome = (): void => {
    if (!welcomeUrl) return;
    void navigator.clipboard.writeText(welcomeUrl);
    setWelcomeCopied(true);
    setTimeout(() => setWelcomeCopied(false), 2000);
  };

  const goToCustomer = (): void => {
    if (!onboarding) return;
    navigate(
      `/customers?returnCustomerId=${onboarding.customerId}&returnTab=details&returnOnboardingId=${onboarding.id}`,
    );
  };

  const goToOpportunity = (): void => {
    if (!onboarding) return;
    navigate(
      `/sales/opportunities/${onboarding.opportunityId}?returnOnboardingId=${onboarding.id}`,
    );
  };

  const goToProject = (): void => {
    if (!onboarding?.projectId) return;
    navigate(`/projects/${onboarding.projectId}?returnOnboardingId=${onboarding.id}`);
  };

  const goToAgreement = (): void => {
    if (!onboarding?.agreementId) return;
    navigate(`/agreements/${onboarding.agreementId}`);
  };

  // New tab so the onboarding screen stays open.
  const goToQuote = (): void => {
    if (!onboarding?.quoteId) return;
    window.open(`/sales/quotes/${onboarding.quoteId}`, '_blank', 'noopener');
  };

  // View-only "SOW for delivery team" page (enabled when the quote is approved).
  const goToSow = (): void => {
    if (!onboarding?.quoteSowToken) return;
    window.open(`/sow/${onboarding.quoteSowToken}`, '_blank', 'noopener');
  };

  const handleDelete = (): void => {
    deleteMutation.mutate(onboardingId, {
      onSuccess: () => {
        setConfirmDelete(false);
        onClose();
        onDeleted?.();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={Boolean(onboardingId)}
        onClose={onClose}
        title={onboarding ? `Onboarding — ${onboarding.customerName}` : 'Onboarding'}
        size="wide"
        fillHeight
        headerActions={
          onboarding ? (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={goToCustomer}>
                Go to customer
              </Button>
              <Button variant="secondary" size="sm" onClick={goToOpportunity}>
                Go to opportunity
              </Button>
              {onboarding.projectId ? (
                <Button variant="secondary" size="sm" onClick={goToProject}>
                  Go to project
                </Button>
              ) : null}
              {onboarding.agreementId ? (
                <Button variant="secondary" size="sm" onClick={goToAgreement}>
                  Go to agreement
                </Button>
              ) : null}
              {onboarding.quoteId ? (
                <Button variant="secondary" size="sm" onClick={goToQuote}>
                  Go to quote ↗
                </Button>
              ) : null}
              {onboarding.quoteSowToken ? (
                <Button variant="secondary" size="sm" onClick={goToSow}>
                  SOW for delivery ↗
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'links', label: 'Links' },
          { id: 'notes', label: 'Notes' },
          { id: 'history', label: 'History' },
        ]}
        activeTabId={tab}
        onTabChange={setTab}
        footer={
          canDelete && onboarding ? (
            <div className="flex w-full items-center justify-between">
              <Button
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirmDelete(true)}
              >
                Delete onboarding
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )
        }
      >
        <DataState
          isLoading={onboardingQuery.isLoading}
          isError={onboardingQuery.isError}
          error={onboardingQuery.error}
          onRetry={() => void onboardingQuery.refetch()}
          isEmpty={false}
        >
          {onboarding ? (
            tab === 'details' ? (
              <div className="space-y-6">
                <ExceptionSection onboarding={onboarding} canEdit={canEdit} />
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <DetailItem label="Customer">{onboarding.customerName}</DetailItem>
                  <DetailItem label="Opportunity">{onboarding.opportunityName}</DetailItem>
                  <DetailItem label="Opportunity type">
                    {onboarding.opportunityServiceCategoryName ?? '—'}
                  </DetailItem>
                  <DetailItem label="Opportunity status">
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <Badge tone={OPPORTUNITY_STATUS_TONES[onboarding.opportunityStatus]}>
                        {OPPORTUNITY_STATUS_LABELS[onboarding.opportunityStatus]}
                      </Badge>
                      {canEdit && onboarding.opportunityStatus !== 'won' ? (
                        <Button
                          variant="secondary"
                          className="px-2 py-1 text-xs"
                          isLoading={updateOpportunity.isPending}
                          onClick={handleMarkOpportunityWon}
                        >
                          Mark won
                        </Button>
                      ) : null}
                    </span>
                    {updateOpportunity.isError ? (
                      <span className="mt-1 block text-xs font-normal text-rose-600">
                        {mutationError(
                          updateOpportunity.error,
                          'Could not mark the opportunity won.',
                        )}
                      </span>
                    ) : null}
                  </DetailItem>
                  <DetailItem label="Onboarding status">
                    <Badge tone={ONBOARDING_STATUS_TONES[onboarding.status]}>
                      {ONBOARDING_STATUS_LABELS[onboarding.status]}
                    </Badge>
                  </DetailItem>
                  {onboarding.preAssignedPmName ? (
                    <DetailItem label="Pre-assigned PM">{onboarding.preAssignedPmName}</DetailItem>
                  ) : null}
                  {onboarding.msGroupName ? (
                    <DetailItem label="MS Group">
                      <span className="block">{onboarding.msGroupName}</span>
                      {onboarding.msGroupEmail ? (
                        <span className="text-xs font-normal text-slate-500">
                          {onboarding.msGroupEmail}
                        </span>
                      ) : null}
                    </DetailItem>
                  ) : null}
                  {onboarding.cuListId ? (
                    <DetailItem label="CU project (ClickUp list)">
                      {onboarding.cuListUrl ? (
                        <a
                          href={onboarding.cuListUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-600 hover:underline"
                        >
                          {onboarding.cuListName ?? 'Open list'}
                        </a>
                      ) : (
                        <span className="block">
                          {onboarding.cuListName ?? onboarding.cuListId}
                        </span>
                      )}
                    </DetailItem>
                  ) : null}
                </dl>

                <div className="rounded-lg border border-slate-200 p-4">
                  <ProgressBar onboarding={onboarding} />
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Delivery phase</span>
                    {onboarding.phase ? (
                      <Badge tone={ONBOARDING_PHASE_TONES[onboarding.phase]}>
                        {ONBOARDING_PHASE_LABELS[onboarding.phase]}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">Not set</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ONBOARDING_PHASES.map((phase) => {
                      const active = onboarding.phase === phase;
                      return (
                        <button
                          key={phase}
                          type="button"
                          disabled={setPhase.isPending}
                          onClick={() =>
                            setPhase.mutate({ id: onboarding.id, phase: active ? null : phase })
                          }
                          className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                            active
                              ? 'border-brand-500 bg-brand-600 text-white'
                              : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {ONBOARDING_PHASE_LABELS[phase]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      Customer welcome link
                    </span>
                    <Badge
                      tone={onboarding.welcomeToken && onboarding.welcomeEnabled ? 'green' : 'gray'}
                    >
                      {onboarding.welcomeToken && onboarding.welcomeEnabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </Badge>
                  </div>
                  {onboarding.welcomeToken && onboarding.welcomeEnabled ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={welcomeUrl}
                          onFocus={(e) => e.target.select()}
                          className="min-w-0 flex-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                        />
                        <Button variant="secondary" size="sm" onClick={handleCopyWelcome}>
                          {welcomeCopied ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Send this private link to the customer to fill out their onboarding form.
                      </p>
                      {canEdit ? (
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmRegenWelcome(true)}
                          >
                            Regenerate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => setConfirmDisableWelcome(true)}
                          >
                            Disable
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500">
                        Generate a private link to send to the customer to fill out their onboarding
                        form (billing, contacts, NetSuite go-live).
                      </p>
                      {canEdit ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          isLoading={regenWelcome.isPending}
                          onClick={() => regenWelcome.mutate(onboarding.id)}
                        >
                          {onboarding.welcomeToken ? 'Re-enable link' : 'Generate welcome link'}
                        </Button>
                      ) : null}
                    </div>
                  )}

                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Welcome form sections
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Toggle a section off to remove it from the customer&apos;s welcome form. The
                      remaining sections renumber automatically.
                    </p>
                    <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {WELCOME_SECTIONS.map((section) => {
                        const enabled = onboarding.welcomeSections.includes(section.key);
                        const isLastEnabled = enabled && onboarding.welcomeSections.length === 1;
                        return (
                          <label
                            key={section.key}
                            className={`flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm ${
                              enabled
                                ? 'border-slate-200 bg-white text-slate-800'
                                : 'border-slate-200 bg-slate-50 text-slate-400'
                            }`}
                          >
                            <span>{section.label}</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              disabled={
                                !canEdit || updateWelcomeSections.isPending || isLastEnabled
                              }
                              title={
                                isLastEnabled
                                  ? 'At least one section must stay enabled.'
                                  : undefined
                              }
                              onClick={() =>
                                updateWelcomeSections.mutate({
                                  id: onboarding.id,
                                  sections: enabled
                                    ? onboarding.welcomeSections.filter(
                                        (key) => key !== section.key,
                                      )
                                    : [...onboarding.welcomeSections, section.key],
                                })
                              }
                              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                enabled ? 'bg-brand-600' : 'bg-slate-300'
                              }`}
                            >
                              <span
                                className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
                                style={{ transform: enabled ? 'translateX(18px)' : 'translateX(2px)' }}
                              />
                            </button>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200">
                  <div className="border-b border-slate-200 px-4 py-2">
                    <span className="text-sm font-medium text-slate-700">Onboarding steps</span>
                  </div>
                  <div className="px-4">
                    {onboarding.steps.map((step) => (
                      <StepRow
                        key={step.key}
                        step={step}
                        canEdit={canEdit}
                        isUpdating={updateStep.isPending}
                        onSetStatus={(status) => handleSetStatus(step.key, status)}
                        onCreate={
                          canEdit && step.key === ONBOARDING_AGREEMENT_STEP
                            ? () => setAgreementOpen(true)
                            : canEdit && step.key === ONBOARDING_PROJECT_STEP
                              ? () => setProjectOpen(true)
                              : canEdit && step.key === 'create_cu_project'
                                ? () => setCuProjectOpen(true)
                                : canEdit && step.key === 'ms_group' && !onboarding.msGroupId
                                  ? () => setMsGroupOpen(true)
                                  : canEdit && step.key === 'welcome_email'
                                    ? () => setWelcomeEmailOpen(true)
                                    : canEdit && step.key === 'sales_rep_info'
                                      ? () => setSalesRepRequestOpen(true)
                                      : canEdit && step.key === 'engage_pmo'
                                        ? () => setEngagePmoOpen(true)
                                        : step.key === 'spam'
                                          ? () => openExternal(SPAM_URL)
                                          : step.key === 'lastpass'
                                            ? () => openExternal(LASTPASS_URL)
                                            : null
                        }
                        createLabel={
                          step.key === 'ms_group'
                            ? 'Create group'
                            : step.key === 'welcome_email'
                              ? 'Send email'
                              : step.key === 'sales_rep_info'
                                ? 'Request info'
                                : step.key === 'engage_pmo'
                                  ? 'Engage PMO'
                                  : step.key === 'create_cu_project'
                                    ? 'Create CU project'
                                    : step.key === 'spam'
                                      ? 'Open SpamTitan'
                                      : step.key === 'lastpass'
                                        ? 'Open LastPass'
                                        : undefined
                        }
                        detail={
                          step.key === 'engage_pmo' && onboarding.preAssignedPmName
                            ? `PM: ${onboarding.preAssignedPmName}`
                            : null
                        }
                        emailSentAt={
                          step.key === 'welcome_email'
                            ? onboarding.welcomeEmailSentAt
                            : step.key === 'sales_rep_info'
                              ? onboarding.salesRepRequestSentAt
                              : step.key === 'engage_pmo'
                                ? onboarding.pmoRequestSentAt
                                : null
                        }
                      />
                    ))}
                  </div>
                </div>

                <p className="text-xs text-slate-400">Created {formatDate(onboarding.createdAt)}</p>
              </div>
            ) : tab === 'links' ? (
              <OnboardingLinksTab onboarding={onboarding} canEdit={canEdit} />
            ) : tab === 'history' ? (
              <HistoryPanel entityType="Onboarding" entityId={onboarding.id} />
            ) : (
              <OnboardingNotesSection onboardingId={onboarding.id} canEdit={canEdit} />
            )
          ) : (
            <div />
          )}
        </DataState>
      </Modal>

      {onboarding ? (
        <>
          <AgreementFormModal
            isOpen={agreementOpen}
            agreement={null}
            defaultCustomerId={onboarding.customerId}
            seedFromOpportunityId={onboarding.opportunityId}
            onCreated={handleAgreementCreated}
            onClose={() => setAgreementOpen(false)}
          />
          <ProjectFormModal
            isOpen={projectOpen}
            project={null}
            defaultCustomerId={onboarding.customerId}
            defaultName={onboarding.opportunityName}
            defaultServiceCategoryId={onboarding.opportunityServiceCategoryId ?? undefined}
            defaultOpportunityId={onboarding.opportunityId}
            defaultProjectManagerId={onboarding.preAssignedPmId ?? undefined}
            onCreated={handleProjectCreated}
            onClose={() => setProjectOpen(false)}
          />
          <MsGroupModal
            isOpen={msGroupOpen}
            onboarding={onboarding}
            onClose={() => setMsGroupOpen(false)}
          />
          <SendWelcomeEmailModal
            isOpen={welcomeEmailOpen}
            onboarding={onboarding}
            onClose={() => setWelcomeEmailOpen(false)}
          />
          <SalesRepRequestModal
            isOpen={salesRepRequestOpen}
            onboarding={onboarding}
            onClose={() => setSalesRepRequestOpen(false)}
          />
          <EngagePmoModal
            isOpen={engagePmoOpen}
            onboarding={onboarding}
            onClose={() => setEngagePmoOpen(false)}
          />
          <CreateCuProjectModal
            isOpen={cuProjectOpen}
            onboarding={onboarding}
            onClose={() => setCuProjectOpen(false)}
          />
        </>
      ) : null}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete onboarding"
        message="Delete this onboarding record and its notes? The linked opportunity and customer are not affected."
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        isOpen={confirmRegenWelcome}
        title="Regenerate welcome link"
        message="Generate a new welcome link? Any link previously shared with the customer will stop working."
        confirmLabel="Regenerate"
        destructive
        isConfirming={regenWelcome.isPending}
        onConfirm={() => {
          if (onboarding) {
            regenWelcome.mutate(onboarding.id, { onSuccess: () => setConfirmRegenWelcome(false) });
          }
        }}
        onCancel={() => setConfirmRegenWelcome(false)}
      />

      <ConfirmDialog
        isOpen={confirmDisableWelcome}
        title="Disable welcome link"
        message="Disable the welcome link? The customer will no longer be able to open the form until it is re-enabled."
        confirmLabel="Disable"
        destructive
        isConfirming={disableWelcome.isPending}
        onConfirm={() => {
          if (onboarding) {
            disableWelcome.mutate(onboarding.id, {
              onSuccess: () => setConfirmDisableWelcome(false),
            });
          }
        }}
        onCancel={() => setConfirmDisableWelcome(false)}
      />
    </>
  );
}
